import React, { useState } from 'react';
import { 
  Sparkles, 
  LogIn, 
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Info
} from 'lucide-react';
import { AuthSession } from '../types';
import { roleNavPermissions } from './Navbar';

interface DemoNoticeBannerProps {
  session: AuthSession | null;
  onSignIn: () => void;
  onSwitchRole: (role: 'owner' | 'admin' | 'operator' | 'viewer') => void;
}

export const DemoNoticeBanner: React.FC<DemoNoticeBannerProps> = ({ 
  session, 
  onSignIn, 
  onSwitchRole
}) => {
  const isDemo = !session?.authenticated || session?.demo_mode;
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('agenticscale_banner_collapsed') === 'true';
    }
    return false;
  });

  const toggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem('agenticscale_banner_collapsed', String(next));
    }
  };

  if (!isDemo) return null;

  const currentRole = session?.organization?.role || 'admin';
  const activeRole = roleNavPermissions[currentRole] || roleNavPermissions.admin;

  return (
    <div className="mb-6 rounded-2xl border border-brand-200/80 bg-white/90 p-3.5 sm:p-4 shadow-xs backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/90 transition-all">
      
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 dark:bg-brand-500/20 px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-brand-700 dark:text-brand-300 border border-brand-200 dark:border-brand-500/30 shrink-0">
            <Sparkles className="h-3 w-3 animate-pulse" />
            <span>Interactive Persona Sandbox</span>
          </span>

          <span className="text-xs text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
            <span className="opacity-70">Active Persona:</span>
            <strong className="text-slate-900 dark:text-white font-bold">{activeRole.icon} {activeRole.label}</strong>
          </span>
        </div>

        {/* Right Actions: Quick Switch Chips, Collapse Toggle, Sign In CTA */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Quick Persona Switcher Chips (Always Accessible on desktop) */}
          <div className="hidden md:flex items-center gap-1">
            {(['admin', 'operator', 'owner', 'viewer'] as const).map((r) => {
              const isSelected = currentRole === r;
              const rInfo = roleNavPermissions[r];
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => onSwitchRole(r)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-bold transition flex items-center gap-1 ${
                    isSelected
                      ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs'
                      : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                  }`}
                  title={`Switch to ${rInfo.label}`}
                >
                  <span>{rInfo.icon}</span>
                  <span>{rInfo.shortLabel}</span>
                </button>
              );
            })}
          </div>

          {/* Collapse/Expand Toggle Button */}
          <button
            type="button"
            onClick={toggleCollapse}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            aria-expanded={!isCollapsed}
            title={isCollapsed ? 'Expand Details' : 'Collapse Details'}
          >
            <span className="hidden sm:inline">{isCollapsed ? 'Details' : 'Hide'}</span>
            {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>

          {/* Sign in CTA */}
          <button
            type="button"
            onClick={onSignIn}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white px-3 py-1.5 text-xs font-bold shadow-xs transition-all"
          >
            <LogIn className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Sign In</span>
          </button>
        </div>
      </div>

      {/* Expandable Body */}
      {!isCollapsed && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-3 animate-fade-in">
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
            The platform navigation menu and access controls dynamically adapt to your selected persona. Switch personas to preview how capabilities, safety lifecycle tools, and restrictions change for different roles.
          </p>

          {/* Mobile Persona Switcher Chips */}
          <div className="flex md:hidden items-center gap-1.5 flex-wrap pt-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Switch Persona:</span>
            {(['admin', 'operator', 'owner', 'viewer'] as const).map((r) => {
              const isSelected = currentRole === r;
              const rInfo = roleNavPermissions[r];
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => onSwitchRole(r)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-bold transition flex items-center gap-1 ${
                    isSelected
                      ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-xs'
                      : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  <span>{rInfo.icon}</span>
                  <span>{rInfo.shortLabel}</span>
                </button>
              );
            })}
          </div>

          {/* Role-Adaptive Feature Explanation Card */}
          <div className={`rounded-xl border p-3.5 text-xs space-y-2.5 ${activeRole.badge}`}>
            <div className="flex items-center gap-2">
              <span className="text-xl shrink-0">{activeRole.icon}</span>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-extrabold text-sm">{activeRole.label}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">— {activeRole.tagline}</span>
                </div>
                <p className="opacity-90 text-[11px] mt-0.5">{activeRole.desc}</p>
              </div>
            </div>

            {/* Feature Scope Breakdown */}
            <div className="pt-2 border-t border-black/10 dark:border-white/10 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
              <div>
                <span className="font-bold flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Visible & Authorized Features:</span>
                </span>
                <ul className="mt-1 space-y-0.5 pl-4 list-disc opacity-90">
                  {activeRole.accessibleSummary.slice(0, 3).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>

              <div>
                <span className="font-bold flex items-center gap-1 opacity-75">
                  <Info className="w-3.5 h-3.5" />
                  <span>Restricted / Omitted for this Role:</span>
                </span>
                <ul className="mt-1 space-y-0.5 pl-4 list-disc opacity-80 italic">
                  {activeRole.restrictedSummary.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
